import { useEffect, useState } from 'react';

import type { MateriaOutputType, MatriculaOutputType } from '@shared/dto';

import { useApiMutation, useApiQuery } from '../../../hooks/use-api';
import { matriculasApi } from '../../../api/matriculas.api';
import { usePainelSnapshot } from '../../../hooks/use-painel-snapshot';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { SITUACAO_MATRICULA_LABEL } from './enum-labels';
import { MatriculaDraftCard, type MatriculaDraft } from './matricula-draft-card';
import { programacaoSemanalVazia, type ProgramacaoSemanal } from './programacao-semanal-grid';
import { programacaoValida } from './programacao-semanal';

function programacaoDosHorarios(
  horarios: Array<{ diaSemana: string; horario: string; desativadoEm: Date | string | null }>,
): ProgramacaoSemanal {
  const programacao = programacaoSemanalVazia();
  for (const horario of horarios) {
    if (horario.desativadoEm === null && horario.diaSemana in programacao) {
      programacao[horario.diaSemana as keyof ProgramacaoSemanal] = {
        frequenta: true,
        horario: horario.horario,
      };
    }
  }
  return programacao;
}

export function MatriculaExistenteCard({
  matricula,
  materias,
  onAtualizada,
}: {
  matricula: MatriculaOutputType;
  materias: MateriaOutputType[];
  onAtualizada: () => void;
}) {
  const { dados } = usePainelSnapshot();
  const professores = dados?.professores ?? [];
  const professorNome = professores.find((p) => p.id === matricula.professorId)?.nome ?? '—';
  const materiaNome = materias.find((m) => m.id === matricula.materiaId)?.nome ?? '—';
  const { data: horarios } = useApiQuery(matriculasApi.listarHorarios, matricula.id);
  const { mutate: atualizarMatricula, loading: salvando } = useApiMutation(matriculasApi.atualizar);
  const [draft, setDraft] = useState<MatriculaDraft | null>(null);
  const [situacao, setSituacao] = useState(matricula.situacao);
  const [observacoes, setObservacoes] = useState(matricula.observacoes ?? '');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!horarios || draft !== null) {
      return;
    }
    setDraft({
      key: matricula.id,
      professorId: matricula.professorId,
      materiaId: matricula.materiaId,
      estagio: matricula.estagio ?? '',
      tipoAtendimento: matricula.tipoAtendimento,
      observacoes: matricula.observacoes ?? '',
      programacao: programacaoDosHorarios(horarios),
    });
  }, [draft, horarios, matricula]);

  async function aoConfirmarEncerramento() {
    setErro(null);
    try {
      await atualizarMatricula({
        id: matricula.id,
        dados: { situacao: 'ENCERRADA', observacoes: observacoes || null },
      });
      onAtualizada();
    } catch (error) {
      setErro(error instanceof Error ? error.message : 'Não foi possível encerrar a matrícula.');
    }
  }

  async function aoSalvar() {
    if (!draft) {
      return;
    }
    setErro(null);
    const professor = professores.find((item) => item.id === draft.professorId);
    if (!draft.materiaId || !professor || !programacaoValida(draft.programacao, professor)) {
      setErro('Revise a disciplina, o professor e a programação semanal.');
      return;
    }

    try {
      await atualizarMatricula({
        id: matricula.id,
        dados: {
          professorId: draft.professorId,
          materiaId: draft.materiaId,
          estagio: draft.estagio || null,
          tipoAtendimento: draft.tipoAtendimento,
          situacao,
          observacoes: observacoes || null,
          horarios: Object.entries(draft.programacao)
            .filter(([, valor]) => valor.frequenta)
            .map(([diaSemana, valor]) => ({
              diaSemana: diaSemana as keyof ProgramacaoSemanal,
              horario: valor.horario,
            })),
        },
      });
      onAtualizada();
    } catch (error) {
      setErro(error instanceof Error ? error.message : 'Não foi possível atualizar a matrícula.');
    }
  }

  async function aoSalvarObservacoesEncerrada() {
    if (observacoes === (matricula.observacoes ?? '')) {
      return;
    }
    try {
      await atualizarMatricula({
        id: matricula.id,
        dados: { observacoes: observacoes || null },
      });
      onAtualizada();
    } catch (error) {
      setErro(
        error instanceof Error ? error.message : 'Não foi possível atualizar as observações.',
      );
    }
  }

  const encerrada = matricula.situacao === 'ENCERRADA';

  return (
    <AccordionItem value={matricula.id}>
      <AccordionTrigger className="px-4">
        <span className="flex flex-1 items-center justify-between gap-2 pr-2">
          <span className="font-medium">
            {materiaNome} · {professorNome}
          </span>
          <Badge variant={matricula.situacao === 'ATIVA' ? 'default' : 'secondary'}>
            {SITUACAO_MATRICULA_LABEL[matricula.situacao]}
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent className="space-y-4 px-4">
        {encerrada ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Matrícula encerrada
              {matricula.motivoEncerramento === 'SUBSTITUICAO' ? ' por substituição' : ''}.
            </p>
            <Label>Observações</Label>
            <Textarea
              value={observacoes}
              onChange={(event) => setObservacoes(event.target.value)}
              onBlur={() => void aoSalvarObservacoesEncerrada()}
            />
          </div>
        ) : draft ? (
          <>
            <MatriculaDraftCard draft={draft} materias={materias} onChange={setDraft} />
            <div className="space-y-2">
              <Label>Situação</Label>
              <Select
                value={situacao}
                onValueChange={(valor) => setSituacao(valor as typeof situacao)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ATIVA">{SITUACAO_MATRICULA_LABEL.ATIVA}</SelectItem>
                  <SelectItem value="PAUSADA">{SITUACAO_MATRICULA_LABEL.PAUSADA}</SelectItem>
                  <SelectItem value="ENCERRADA">{SITUACAO_MATRICULA_LABEL.ENCERRADA}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={observacoes}
                onChange={(event) => setObservacoes(event.target.value)}
              />
            </div>
            {erro ? <p className="text-sm text-destructive">{erro}</p> : null}
            <div className="flex justify-end">
              {situacao === 'ENCERRADA' ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" disabled={salvando}>
                      {salvando ? 'Encerrando...' : 'Encerrar matrícula'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Encerrar esta matrícula?</AlertDialogTitle>
                      <AlertDialogDescription>
                        A matrícula sairá da agenda e não poderá ser reativada. Alterações feitas
                        nos demais campos não serão aplicadas.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => void aoConfirmarEncerramento()}
                        disabled={salvando}
                      >
                        Encerrar matrícula
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                <Button type="button" onClick={() => void aoSalvar()} disabled={salvando}>
                  {salvando ? 'Salvando...' : 'Salvar matrícula'}
                </Button>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Carregando programação...</p>
        )}
        {encerrada && erro ? <p className="text-sm text-destructive">{erro}</p> : null}
      </AccordionContent>
    </AccordionItem>
  );
}
